import { authService } from './auth-service';

async function apiFetch(path: string, init: RequestInit): Promise<Response> {
  await authService.ensureFreshToken();
  const token = authService.getToken();
  return fetch(`/api/ticket${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
}

export type TicketAction = 'CHECK_IN' | 'CHECK_OUT';
export type TicketStatus = 'ACTIVE' | 'DONE';
export type NotifyStatus = 'SENT' | 'NUM-OPT-OUT' | 'ERROR' | 'NO_PHONE';

export interface TicketActionResult {
  ticketId: string;
  status: TicketStatus;
  notifyStatus?: NotifyStatus;
  notifyMessage?: string;
}

async function submitOrgTicketAction(
  orgId: string,
  ticketNum: string,
  action: TicketAction,
  phoneNum?: string,
  subscriptionToken?: string,
  optInAcceptedTS?: string | null,
): Promise<TicketActionResult> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/ticket`, {
    method: 'POST',
    body: JSON.stringify({
      ticketNum,
      action,
      phoneNum: phoneNum || null,
      subscriptionToken: subscriptionToken || null,
      optInAcceptedTS: optInAcceptedTS ?? null,
    }),
  });
  return handleTicketResponse(res);
}

async function submitAnonTicketAction(
  accessToken: string,
  ticketNum: string,
  action: TicketAction,
  phoneNum?: string,
  subscriptionToken?: string,
  optInAcceptedTS?: string | null,
): Promise<TicketActionResult> {
  const res = await fetch(`/api/ticket/v1/public/token/${encodeURIComponent(accessToken)}/ticket`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ticketNum,
      action,
      phoneNum: phoneNum || null,
      subscriptionToken: subscriptionToken || null,
      optInAcceptedTS: optInAcceptedTS ?? null,
    }),
  });
  return handleTicketResponse(res);
}

async function getSubscriptionTokenStatus(token: string): Promise<{ status: string; canSubscribe: boolean }> {
  const res = await fetch(`/api/ticket/v1/public/subscribe/${encodeURIComponent(token)}/status`);
  if (res.status === 404) throw new Error('token_not_found');
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function subscribe(token: string, phoneNum: string): Promise<void> {
  const res = await fetch(`/api/ticket/v1/public/subscribe/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNum }),
  });
  if (res.status === 404) throw new Error('token_not_found');
  if (res.status === 410) throw new Error('token_spent');
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
}

async function handleTicketResponse(res: Response): Promise<TicketActionResult> {
  if (res.status === 409) throw new Error('already_checked_in');
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function provisionTokens(orgId: string, count: number): Promise<string[]> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/tokens/provision`, {
    method: 'POST',
    body: JSON.stringify({ count }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  const data: { tokens: string[] } = await res.json();
  return data.tokens;
}

export type TicketStatusValue = 'CHECKED-IN' | 'CHECKED-OUT' | 'DONE' | 'NOTIFY_ERROR';

export interface Ticket {
  ticketId: string;
  ticketNum: string;
  status: TicketStatusValue;
  active: boolean;
  phoneNum?: string | null;
  createdTS: string;
  updatedTS: string;
}

async function getTickets(
  orgId: string,
  params?: { ticketNum?: string; activeOnly?: boolean; from?: string; to?: string },
): Promise<Ticket[]> {
  const qs = new URLSearchParams();
  if (params?.ticketNum)              qs.set('ticketNum', params.ticketNum);
  if (params?.activeOnly !== undefined) qs.set('activeOnly', String(params.activeOnly));
  if (params?.from)                   qs.set('from', params.from);
  if (params?.to)                     qs.set('to', params.to);
  const query = qs.toString() ? `?${qs}` : '';
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/tickets${query}`, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  const data: { tickets: Ticket[] } = await res.json();
  return data.tickets;
}

export type ActivityEventType = 'CHECK_IN' | 'CHECK_OUT' | 'SMS_SENT' | 'SMS_FAILED';

export interface ActivityLogEntry {
  logId: string;
  orgId: string;
  eventType: ActivityEventType;
  ticketId: string;
  ticketNum: string;
  phoneNum: string | null;
  createdTS: string;
}

export interface ActivityLogDetail {
  logId: string;
  orgId: string;
  eventType: ActivityEventType;
  ticketId: string;
  ticketNum: string;
  phoneNum: string | null;
  errorMsg: string | null;
  createdTS: string;
}

async function getActivityLog(orgId: string, logId: string): Promise<ActivityLogDetail> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/activity-logs/${encodeURIComponent(logId)}`, {
    method: 'GET',
  });
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function getActivityLogs(
  orgId: string,
  params?: { eventType?: string; from?: string; to?: string; limit?: number },
): Promise<ActivityLogEntry[]> {
  const qs = new URLSearchParams();
  if (params?.eventType) qs.set('eventType', params.eventType);
  if (params?.from)      qs.set('from', params.from);
  if (params?.to)        qs.set('to', params.to);
  if (params?.limit)     qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs}` : '';
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/activity-logs${query}`, {
    method: 'GET',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  const data: { logs: ActivityLogEntry[] } = await res.json();
  return data.logs;
}

export interface TextMessageTemplate {
  template: string;
  modifiedTS?: string;
  modifiedBy?: string;
}

async function getTextMessageTemplate(orgId: string): Promise<TextMessageTemplate> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/text-message-template`, {
    method: 'GET',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function updateTextMessageTemplate(orgId: string, template: string): Promise<TextMessageTemplate> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/text-message-template`, {
    method: 'PUT',
    body: JSON.stringify({ template }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export const ticketService = { submitOrgTicketAction, submitAnonTicketAction, subscribe, getSubscriptionTokenStatus, provisionTokens, getActivityLogs, getActivityLog, getTextMessageTemplate, updateTextMessageTemplate, getTickets };